import React, { useEffect, useRef, useState } from 'react'
import { Prompt, useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Add from '@mui/icons-material/Add'
import Typography from '@mui/material/Typography'

import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup, { FORM_WIDE_UPLOAD_BUTTON_SX } from '../../component/BottomButtonGroup'
import { FORM_IMAGE_PREVIEW_FRAME_SX, FORM_PANEL_SX, getFormChoiceCardSx } from '../../component/formSurfaceStyles'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import {
    APP_PARTNER_IMAGE_FILE_ACCEPT,
    APP_PARTNER_IMAGE_FILE_ERROR_MESSAGE,
    applyAppPartnerDraft,
    clearAppPartnerDraft,
    createAppPartnerDraftPayload,
    isAppPartnerImageFile,
    NEW_APP_PARTNER_DRAFT_KEY,
    readAppPartnerDraft,
    saveAppPartnerDraft,
} from './appPartnerDraftUtils'
import {
    APP_PARTNER_IMAGE_LEAVE_MESSAGE,
    hasPendingAppPartnerImage,
    usePendingAppPartnerImageUnloadGuard,
} from './appPartnerLeaveGuard'

const PANEL_SX = FORM_PANEL_SX

const IMAGE_PREVIEW_FRAME_SX = {
    ...FORM_IMAGE_PREVIEW_FRAME_SX,
    width: '100%',
    height: { xs: 220, md: 280 },
    mb: 1.5,
}

const IMAGE_PREVIEW_SX = {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
}

const getChoiceCardSx = (selected, disabled = false) => ({
    ...getFormChoiceCardSx(selected, disabled),
    p: 1.5,
    width: '100%',
})

const IDENTITY_OPTIONS = [
    {
        value: 0,
        title: '불필요',
        description: '본인인증 없이 제휴사 약관 화면을 진행합니다.',
    },
    {
        value: 1,
        title: '필요',
        description: '제휴사 약관 진행 전 본인인증을 요구합니다.',
    },
]

const getImageFileName = (imageFile) => {
    if (!imageFile || typeof imageFile !== 'object') {
        return ''
    }

    return imageFile.name || ''
}

function FormSection({ title, description, children }) {
    return (
        <Paper variant="outlined" sx={PANEL_SX}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {description}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {children}
            </Box>
        </Paper>
    )
}

function ChoiceCardLabel({ title, description }) {
    return (
        <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {description}
            </Typography>
        </Box>
    )
}

// 카드형 선택 UI에서도 기존 폼 변경 핸들러가 받던 name/value 이벤트 계약을 유지한다.
const createFieldChangeEvent = (name, value) => ({
    target: {
        name,
        value,
    },
})

function SettingChoiceGroup({ title, name, value, options, disabled, onChange }) {
    return (
        <FormControl fullWidth>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {title}
            </Typography>
            <Box
                role="radiogroup"
                aria-label={title}
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                    gap: 1,
                    mt: 1,
                }}
            >
                {options.map((option) => {
                    const checked = String(value) === String(option.value)

                    return (
                        <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={(
                                <Radio
                                    name={name}
                                    checked={checked}
                                    onChange={() => onChange(createFieldChangeEvent(name, option.value))}
                                    disabled={disabled}
                                />
                            )}
                            disabled={disabled}
                            sx={getChoiceCardSx(checked, disabled)}
                            label={(
                                <ChoiceCardLabel
                                    title={option.title}
                                    description={option.description}
                                />
                            )}
                        />
                    )
                })}
            </Box>
        </FormControl>
    )
}

function PartnerLogoUploadPanel({
    imageSrc,
    imageFile,
    disabled,
    onUploadClick,
}) {
    const imageFileName = getImageFileName(imageFile)

    return (
        <Paper variant="outlined" sx={PANEL_SX}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    로고 이미지
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    제휴사 화면에 노출되는 로고 이미지를 등록합니다.
                </Typography>
            </Box>
            <Box sx={IMAGE_PREVIEW_FRAME_SX}>
                {imageSrc ? (
                    <Box
                        component="img"
                        src={imageSrc}
                        alt="로고 이미지 미리보기"
                        sx={IMAGE_PREVIEW_SX}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        이미지를 선택하면 미리보기가 표시됩니다.
                    </Typography>
                )}
            </Box>
            <FormControl fullWidth>
                <TextField
                    inputProps={{ accept: APP_PARTNER_IMAGE_FILE_ACCEPT }}
                    style={{ display: 'none' }}
                    name="image"
                    id="image"
                    type="file"
                    disabled={disabled}
                    onChange={onUploadClick}
                />
                <Box component="label" htmlFor="image" sx={{ display: 'block' }}>
                    <Button
                        fullWidth
                        variant="contained"
                        color="success"
                        component="span"
                        startIcon={<Add />}
                        sx={FORM_WIDE_UPLOAD_BUTTON_SX}
                        disabled={disabled}
                    >
                        이미지 선택
                    </Button>
                </Box>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {imageFileName ? `선택한 파일: ${imageFileName}` : '저장 전 미리보기로 이미지가 올바른지 확인하세요.'}
            </Typography>
        </Paper>
    )
}

export default function NewAppPartner() {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const curPage = locationState.curPage || 1
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect || { searchStatus: '' }
    const listSearch = location.search || ''
    const [saving, setSaving] = useState(false)
    const [params, setParams] = useState({
        partnerName: '',
        image: '',
        title: '',
        content: '',
        isIdentity: 1,
    })
    const [image, setImage] = useState('')
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [draftReady, setDraftReady] = useState(false)
    const [navigationUnlocked, setNavigationUnlocked] = useState(false)
    const initialDraftSnapshotRef = useRef('')
    const leaveGuardEnabled = hasPendingAppPartnerImage(params) && !saving && !navigationUnlocked

    usePendingAppPartnerImageUnloadGuard(leaveGuardEnabled)

    useEffect(() => {
        const baseParams = {
            partnerName: '',
            image: '',
            title: '',
            content: '',
            isIdentity: 1,
        }
        const storedDraft = readAppPartnerDraft(NEW_APP_PARTNER_DRAFT_KEY)
        const restoredDraft = applyAppPartnerDraft({
            baseParams,
            draft: storedDraft,
        })

        initialDraftSnapshotRef.current = JSON.stringify(createAppPartnerDraftPayload({
            params: baseParams,
        }))

        setParams(prevParams => ({
            ...prevParams,
            ...restoredDraft.params,
        }))
        setDraftReady(true)
    }, [])

    useEffect(() => {
        if (!draftReady) {
            return
        }

        // 제휴사 등록은 목록 query를 건드리지 않고 폼 draft만 sessionStorage에 보존한다.
        const nextDraft = createAppPartnerDraftPayload({
            params,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearAppPartnerDraft(NEW_APP_PARTNER_DRAFT_KEY)
            return
        }

        saveAppPartnerDraft(NEW_APP_PARTNER_DRAFT_KEY, nextDraft)
    }, [draftReady, params])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const moveToList = (snackbarState) => {
        setNavigationUnlocked(true)
        clearAppPartnerDraft(NEW_APP_PARTNER_DRAFT_KEY)

        // 저장 후 이동은 이탈 경고를 잠시 풀고, 목록 조건은 그대로 유지한다.
        setTimeout(() => {
            history.push(createListLinkTarget({
                pathname: `/appPartners`,
                search: listSearch,
                ...(snackbarState ? {
                    openSnackbar: true,
                    severitySnackbar: snackbarState.severity,
                    messageSnackbar: snackbarState.message,
                } : {}),
                curPage: curPage,
                searchText: searchText,
                searchSelect: searchSelect
            }))
        }, 0)
    }

    const handleBackClick = (event) => {
        if (!leaveGuardEnabled) {
            clearAppPartnerDraft(NEW_APP_PARTNER_DRAFT_KEY)
            return
        }

        event.preventDefault()

        if (!window.confirm(APP_PARTNER_IMAGE_LEAVE_MESSAGE)) {
            return
        }

        moveToList()
    }

    const handleUpdateField = (e) => {
        const { name, value } = e.target

        setParams(prevParams => ({
            ...prevParams,
            [name]: name === 'isIdentity' ? Number(value) : value
        }))
    }

    const handleUploadClick = (e) => {
        e.preventDefault()
        const { name, files } = e.target
        const file = files[0]

        if (!file) {
            return
        }

        if (!isAppPartnerImageFile(file)) {
            e.target.value = ''
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(APP_PARTNER_IMAGE_FILE_ERROR_MESSAGE)
            return
        }

        let reader = new FileReader()

        reader.onloadend = () => {
            setParams(prevParams => ({
                ...prevParams,
                [name]: file
            }))

            setImage(reader.result)
        }
    
        reader.readAsDataURL(file)
    }

    const createData = async (params) => {
        try {
            setSaving(true)

            const { data } = await API({
                method: 'post',
                url: '/api/v1/admin/appPartner/create',
                data: params,
                headers: { "Content-Type": "multipart/form-data" }
            })

            if (data.code === 200) {
                // 등록 후에도 이전 제휴사 목록 조건으로 그대로 복귀시킨다.
                moveToList({
                    severity: 'success',
                    message: data.message,
                })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('제휴사를 등록하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        const nextPartnerName = String(params.partnerName || '').trim()
        const nextTitle = String(params.title || '').trim()
        const nextContent = String(params.content || '').trim()

        setParams(prevParams => ({
            ...prevParams,
            partnerName: nextPartnerName,
            title: nextTitle,
            content: nextContent
        }))

        if (!params.image) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('로고 이미지를 업로드해주세요.')
            return
        }

        if (!nextPartnerName) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('제휴사명을 입력하세요.')
            return
        }

        if (!nextTitle) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('타이틀을 입력하세요.')
            return
        }

        if (!nextContent) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('약관 내용을 입력하세요.')
            return
        }

        const formData = new FormData()
        formData.append('image',params.image)
        formData.append('partnerName',nextPartnerName)
        formData.append('title',nextTitle)
        formData.append('content',nextContent)
        formData.append('isIdentity',params.isIdentity)

        createData(formData)
    }
    
    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>제휴사 등록</Title>
                <Prompt when={leaveGuardEnabled} message={APP_PARTNER_IMAGE_LEAVE_MESSAGE} />
                <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }}>
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        <Grid item xs={12} md={4}>
                            <PartnerLogoUploadPanel
                                imageSrc={image}
                                imageFile={params.image}
                                disabled={saving}
                                onUploadClick={handleUploadClick}
                            />
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <FormSection
                                title="제휴사 정보"
                                description="제휴사명, 타이틀, 약관 내용을 작성합니다."
                            >
                                <FormControl fullWidth>
                                    <TextField
                                        label="제휴사명"
                                        required
                                        id="partnerName"
                                        name="partnerName"
                                        value={params.partnerName}
                                        onChange={handleUpdateField}
                                        disabled={saving}
                                        size="small"
                                    />
                                </FormControl>
                                <FormControl fullWidth>
                                    <TextField
                                        label="타이틀"
                                        required
                                        id="title"
                                        name="title"
                                        value={params.title}
                                        onChange={handleUpdateField}
                                        disabled={saving}
                                        size="small"
                                    />
                                </FormControl>
                                <FormControl fullWidth>
                                    <TextField
                                        label="약관 내용"
                                        required
                                        id="content"
                                        name="content"
                                        multiline
                                        rows={13}
                                        value={params.content}
                                        onChange={handleUpdateField}
                                        disabled={saving}
                                        size="small"
                                    />
                                </FormControl>
                            </FormSection>
                        </Grid>
                        <Grid item xs={12}>
                            <FormSection
                                title="본인인증 설정"
                                description="제휴사 약관 진행 전 본인인증 요구 여부를 설정합니다."
                            >
                                <SettingChoiceGroup
                                    title="본인인증여부"
                                    name="isIdentity"
                                    value={params.isIdentity ?? 1}
                                    options={IDENTITY_OPTIONS}
                                    disabled={saving}
                                    onChange={handleUpdateField}
                                />
                            </FormSection>
                        </Grid>
                    </Grid>
                    <BottomButtonGroup path="/appPartners" search={listSearch} visible={true} curPage={curPage} searchText={searchText} searchSelect={searchSelect} submitDisabled={saving} submitLabel={saving ? '저장 중...' : '저장'} onBackClick={handleBackClick} />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
